import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import type { MeResponse } from '@teraseya/api-types';
import { ApiError, apiFetch } from '../../lib/api-client';
import { clearToken, ME_QUERY_KEY } from '../../lib/auth';
import { Button, Card, Field, Spinner } from '../../components/ds';

/**
 * Admin gate runs server-side via the `me` query. We fetch through the
 * shared TanStack Query client (route context.queryClient) so the same
 * cache feeds the nav-link admin badge and any sibling admin route.
 *
 * - 401 → wipe the token and bounce to /login (the token is dead)
 * - role !== ADMIN → bounce to /
 * - else render
 */
export const Route = createFileRoute('/admin/design-system')({
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.fetchQuery({
        queryKey: ME_QUERY_KEY,
        queryFn: () => apiFetch<MeResponse>('/private/auth/me'),
        staleTime: 60_000,
      });
      if (me.role !== 'ADMIN') {
        throw redirect({ to: '/' });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearToken();
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: DesignSystemPage,
});

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: ButtonVariant[] = ['primary', 'secondary', 'ghost', 'danger'];
const SIZES: ButtonSize[] = ['sm', 'md', 'lg'];

function DesignSystemPage() {
  return (
    <div className="space-y-12">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">Design System</h1>
        <p className="text-text-muted">
          Round 1 surface: Button, Card, Field. Every component on this page is
          an instance of the components it documents.
        </p>
      </header>

      <ButtonsSection />
      <CardSection />
      <FieldsSection />
      <LoginExampleSection />
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {description && <p className="text-sm text-text-muted">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function ButtonsSection() {
  return (
    <Section
      title="Buttons"
      description="variant × size matrix, plus disabled and loading states."
    >
      {VARIANTS.map((variant) => (
        <div key={variant} className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wide text-text-muted">
            variant={variant}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {SIZES.map((size) => (
              <Button key={size} variant={variant} size={size}>
                {variant} / {size}
              </Button>
            ))}
          </div>
        </div>
      ))}
      <div className="space-y-2">
        <p className="text-xs font-mono uppercase tracking-wide text-text-muted">
          disabled
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {VARIANTS.map((variant) => (
            <Button key={variant} variant={variant} disabled>
              {variant} disabled
            </Button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-mono uppercase tracking-wide text-text-muted">
          loading (composed via &lt;Spinner /&gt;)
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled>
            <Spinner /> Saving…
          </Button>
          <Button variant="secondary" disabled>
            <Spinner /> Loading
          </Button>
        </div>
      </div>
    </Section>
  );
}

function CardSection() {
  return (
    <Section
      title="Card"
      description="Compound component. Use only the slots you need."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Card.Root>
          <Card.Header>
            <Card.Title>Minimal</Card.Title>
          </Card.Header>
          <Card.Body>
            <p className="text-sm">Just a Title and a Body — no Footer.</p>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Card.Title>Devices</Card.Title>
            <Card.Description>Sensors you manage.</Card.Description>
          </Card.Header>
          <Card.Body>
            <p className="text-sm">
              Full surface with Header, Description, Body, and Footer actions.
            </p>
          </Card.Body>
          <Card.Footer>
            <Button variant="secondary">Cancel</Button>
            <Button>Save</Button>
          </Card.Footer>
        </Card.Root>

        <Card.Root className="md:col-span-2">
          <Card.Header>
            <Card.Title>Form inside a Card</Card.Title>
            <Card.Description>
              Compose Field inside Card.Body — no special wiring needed.
            </Card.Description>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4 max-w-md">
              <Field.Root>
                <Field.Label>Display name</Field.Label>
                <Field.Input placeholder="Jane Cooper" />
                <Field.Hint>Shown to other members.</Field.Hint>
              </Field.Root>
            </div>
          </Card.Body>
          <Card.Footer>
            <Button variant="ghost">Discard</Button>
            <Button>Save changes</Button>
          </Card.Footer>
        </Card.Root>
      </div>
    </Section>
  );
}

function FieldsSection() {
  return (
    <Section
      title="Form fields"
      description="Field auto-wires label/input/hint/error ids via context."
    >
      <div className="grid gap-6 max-w-xl">
        <Field.Root>
          <Field.Label>Plain</Field.Label>
          <Field.Input placeholder="Type here" />
        </Field.Root>

        <Field.Root>
          <Field.Label>With hint</Field.Label>
          <Field.Input type="email" placeholder="you@example.com" />
          <Field.Hint>We never share this.</Field.Hint>
        </Field.Root>

        <Field.Root error="Must be a valid email address.">
          <Field.Label>With error</Field.Label>
          <Field.Input type="email" defaultValue="not-an-email" />
          <Field.Error />
        </Field.Root>

        <Field.Root>
          <Field.Label>Textarea</Field.Label>
          <Field.Textarea
            rows={4}
            placeholder="Tell us about your operation"
          />
          <Field.Hint>Markdown is not supported (yet).</Field.Hint>
        </Field.Root>
      </div>
    </Section>
  );
}

/**
 * Realistic compound usage so the page-as-spec includes a full form. NOT a
 * working login — this is a render-only sample. The /login route does the
 * real thing.
 */
function LoginExampleSection() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const showError = email.length > 0 && !email.includes('@');

  return (
    <Section
      title="Login form (composition example)"
      description="The /login page is the production version of this — same primitives."
    >
      <div className="max-w-[420px]">
        <Card.Root>
          <Card.Header>
            <Card.Title>Log in</Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <Field.Root error={showError ? 'Enter a valid email.' : undefined}>
                <Field.Label>Email</Field.Label>
                <Field.Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Field.Error />
              </Field.Root>
              <Field.Root>
                <Field.Label>Password</Field.Label>
                <Field.Input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field.Root>
            </div>
          </Card.Body>
          <Card.Footer className="justify-stretch">
            <Button type="button" className="w-full">
              Log in
            </Button>
          </Card.Footer>
        </Card.Root>
      </div>
    </Section>
  );
}
